/**
 * @author claude
 * @date 2026.03.05
 * @description 가맹점별 판매 통계 필터 컴포넌트
 */
import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useLoginStore } from '../../../../store/useLogin';
import { useFranchiseStore } from '../../../../store/useFranchise';

import DatePickerInput from '../../../form/DatePickerInput';
import { formatDate } from '../../../../util/util';

interface ISalesStatisticsFilter {
  getFilteredData: (params: any) => void;
  onFranchiseChange?: (franchiseId: string) => void;
  onExcelDownload?: () => void;
  excelDownloading?: boolean;
}

const SalesStatisticsFilter = ({
  getFilteredData,
  onFranchiseChange,
  onExcelDownload,
  excelDownloading,
}: ISalesStatisticsFilter) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useLoginStore();
  const { franchises } = useFranchiseStore();

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [selectedFranchiseId, setSelectedFranchiseId] = useState<string>('');
  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState<boolean>(false);
  const [franchiseSearchText, setFranchiseSearchText] = useState<string>('');

  const [startDate, setStartDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.startDt) {
      return new Date(currentQuery.startDt as string);
    }
    const today = new Date();
    return new Date(today.setDate(today.getDate() - 30));
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.endDt) {
      return new Date(currentQuery.endDt as string);
    }
    return new Date();
  });

  useEffect(() => {
    const currentQuery = queryString.parse(location.search);

    if (currentQuery.franchiseId) {
      setSelectedFranchiseId(currentQuery.franchiseId as string);
    } else {
      setSelectedFranchiseId('');
    }

    if (currentQuery.startDt) {
      setStartDate(new Date(currentQuery.startDt as string));
    } else {
      const today = new Date();
      setStartDate(new Date(today.setDate(today.getDate() - 30)));
    }
    if (currentQuery.endDt) {
      setEndDate(new Date(currentQuery.endDt as string));
    } else {
      setEndDate(new Date());
    }
  }, [location.search]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.franchise-dropdown-container')) {
        setShowFranchiseDropdown(false);
      }
    };
    if (showFranchiseDropdown) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showFranchiseDropdown]);

  const updateQueryString = useCallback(
    (updatedFields: Record<string, string | undefined>) => {
      const currentQuery = queryString.parse(location.search);
      const updatedQuery = {
        ...currentQuery,
        ...updatedFields,
      };

      const newQueryString = queryString.stringify(updatedQuery);
      navigate(`?${newQueryString}`);
    },
    [location.search, navigate]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      setStartDate(date);
    },
    []
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      setEndDate(date);
    },
    []
  );

  const handleFranchiseSelect = useCallback(
    (franchiseId: string) => {
      setSelectedFranchiseId(franchiseId);
      setShowFranchiseDropdown(false);
      setFranchiseSearchText('');
      updateQueryString({ franchiseId: franchiseId || undefined });

      if (onFranchiseChange) {
        onFranchiseChange(franchiseId);
      }
    },
    [updateQueryString, onFranchiseChange]
  );

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const params = {
        franchiseId: selectedFranchiseId,
        startDt: startDate
          ? formatDate(startDate)['yyyy-mm-dd']
          : undefined,
        endDt: endDate
          ? formatDate(endDate)['yyyy-mm-dd']
          : undefined,
      };

      getFilteredData(params);
    },
    [getFilteredData, selectedFranchiseId, startDate, endDate]
  );

  return (
    <div className="!w-full">
      <div className="!flex !justify-between !items-center">
        <button
          onClick={() => setIsFilterOpen(prev => !prev)}
          className="md:!hidden !px-3 !py-1.5 !border !rounded !text-sm"
        >
          {isFilterOpen ? '닫기' : '검색필터 열기'}
        </button>
      </div>
      <form
        onSubmit={handleSearch}
        className={`
          ${isFilterOpen ? '!flex' : '!hidden'}
          md:!flex !mt-4 !flex-col !gap-4 md:!flex-row md:!items-center md:!justify-between !w-full
        `}
      >
        <div className="!flex !items-center !gap-4 !flex-wrap">
          <span className="!text-lg !font-semibold !text-gray-800">가맹점별 판매 통계</span>
          <div className="!flex !items-center !gap-1">
            <DatePickerInput
              value={startDate}
              onChange={handleStartDateChange}
              placeholder="시작일"
              width={130}
            />
            <span className="!text-gray-400">~</span>
            <DatePickerInput
              value={endDate}
              onChange={handleEndDateChange}
              placeholder="종료일"
              width={130}
            />
          </div>

          {role === 'ROLE_ADMIN' && (
            <>
              {/* 가맹점 선택 드롭다운 */}
              <div className="!flex !items-center !gap-2">
                <span className="!text-sm !font-medium !text-gray-700">가맹점:</span>
                <div className="!relative franchise-dropdown-container">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowFranchiseDropdown(!showFranchiseDropdown);
                    }}
                    className="!flex !items-center !gap-2 !px-4 !py-2 !bg-white !border !border-gray-300 !rounded-lg !text-sm !font-medium !text-gray-700 hover:!bg-gray-50 !transition-colors"
                  >
                    <span>
                      {selectedFranchiseId
                        ? franchises?.find(f => f.id === selectedFranchiseId)?.companyName || '전체'
                        : '전체'}
                    </span>
                    <svg className="!w-4 !h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showFranchiseDropdown && (
                    <div className="!absolute !left-0 !mt-2 !w-64 !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-10">
                      {/* 검색 입력창 */}
                      <div className="!p-3 !border-b !border-gray-200">
                        <div className="!relative">
                          <input
                            type="text"
                            value={franchiseSearchText}
                            onChange={(e) => setFranchiseSearchText(e.target.value)}
                            placeholder="가맹점명으로 검색..."
                            className="!w-full !pl-8 !pr-3 !py-2 !text-sm !border !border-gray-300 !rounded-md focus:!outline-none focus:!ring-2 focus:!ring-blue-500 focus:!border-transparent"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <svg className="!absolute !left-2.5 !top-1/2 !transform !-translate-y-1/2 !w-4 !h-4 !text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>

                      <div className="!max-h-60 !overflow-y-auto">
                        {franchises && franchises.length > 0 ? (
                          franchises
                            .filter(franchise => {
                              if (!franchiseSearchText) return true;
                              return franchise.companyName && franchise.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase());
                            })
                            .map((franchiseItem) => (
                              <button
                                type="button"
                                key={franchiseItem.id}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleFranchiseSelect(franchiseItem.id);
                                }}
                                className={`!w-full !px-4 !py-2 !text-left !text-sm !transition-colors ${
                                  selectedFranchiseId === franchiseItem.id
                                    ? '!bg-blue-50 !text-blue-700'
                                    : '!text-gray-700 hover:!bg-gray-100'
                                }`}
                              >
                                <div className="!font-medium">{franchiseItem.companyName || ''}</div>
                                <div className="!text-xs !text-gray-500">{franchiseItem.ceo || '-'} | {franchiseItem.telNo || '-'}</div>
                              </button>
                            ))
                        ) : (
                          <div className="!px-4 !py-3 !text-sm !text-gray-500">가맹점 목록이 없습니다.</div>
                        )}
                        {franchises && franchises.filter(franchise => {
                          if (!franchiseSearchText) return true;
                          return franchise.companyName && franchise.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase());
                        }).length === 0 && franchiseSearchText && (
                          <div className="!px-4 !py-3 !text-sm !text-gray-500">검색 결과가 없습니다.</div>
                        )}
                      </div>
                      <div className="!border-t !border-gray-200 !px-4 !py-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFranchiseSelect('');
                          }}
                          className="!text-sm !text-red-600 hover:!text-red-700"
                        >
                          전체 보기
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <button
            type="submit"
            className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-700 !text-white !rounded-lg !text-sm !font-medium !flex !items-center !gap-2 !transition-colors"
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} className="!text-sm" />
            검색
          </button>

          {onExcelDownload && (
            <button
              type="button"
              onClick={onExcelDownload}
              disabled={excelDownloading}
              className="!px-4 !py-2 !bg-green-500 !text-white !rounded-lg hover:!bg-green-600 disabled:!bg-green-300 disabled:!cursor-not-allowed !transition-colors !text-sm !font-medium"
            >
              {excelDownloading ? '다운로드 중...' : '엑셀 다운로드'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default SalesStatisticsFilter;
